import { useState } from 'react';
import { trpc } from '@/lib/trpc';
import { ui, errText, type Answer, type Choice, type QType } from './envUi';

interface Question { type: QType; en: string; ar: string }
type Screen = 'front' | 'question' | 'end';

/**
 * Buttons shown for each question type. "Yes / Yes" has two Yes buttons — one
 * green, one red — and both record as agree.
 */
function choicesFor(type: QType): { label: string; tone: 'agree' | 'disagree'; value: Choice }[] {
  if (type === 'agreedisagree') {
    return [
      { label: 'Agree',    tone: 'agree',    value: 'agree' },
      { label: 'Disagree', tone: 'disagree', value: 'disagree' },
    ];
  }
  if (type === 'yesno') {
    return [
      { label: 'Yes / نعم', tone: 'agree',    value: 'agree' },
      { label: 'No / لا',   tone: 'disagree', value: 'disagree' },
    ];
  }
  return [
    { label: 'Yes / نعم', tone: 'agree',    value: 'agree' },
    { label: 'Yes / نعم', tone: 'disagree', value: 'agree' },
  ];
}

function shuffled<T>(items: T[]): T[] {
  const a = [...items];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export default function SurveyGame() {
  const [screen, setScreen] = useState<Screen>('front');
  const [form, setForm]     = useState({ name: '', phone: '', major: '', id: '' });
  const [formError, setFormError] = useState('');

  const [questions, setQuestions] = useState<Question[]>([]);
  const [index, setIndex]         = useState(0);
  // Index of the pressed button (0 or 1). Two buttons can carry the same value.
  const [selectedIdx, setSelectedIdx] = useState<0 | 1 | null>(null);
  const [answers, setAnswers]     = useState<Answer[]>([]);

  const { data: pool } = trpc.questionnaire.questions.useQuery();
  const submit     = trpc.questionnaire.submit.useMutation({
    onSuccess: () => setScreen('end'),
  });

  const reset = () => {
    setForm({ name: '', phone: '', major: '', id: '' });
    setFormError('');
    setQuestions([]);
    setAnswers([]);
    setIndex(0);
    setSelectedIdx(null);
    setScreen('front');
  };

  const handleStart = async (e: React.FormEvent) => {
    e.preventDefault();
    const name = form.name.trim(), phone = form.phone.trim(), major = form.major.trim(), id = form.id.trim();
    if (!name || !phone || !major || !id) { setFormError('Please fill in Name, Number, Major, and Student ID.'); return; }
    if (!/^\d{8}$/.test(phone)) { setFormError('Please write a valid phone number.'); return; }
    setFormError('');

    if (!pool) { setFormError('Questions are still loading — try again in a moment.'); return; }
    const count  = Math.min(pool.randomCount, pool.statements.length);
    const picked = shuffled(pool.statements)
      .slice(0, count)
      .map<Question>(s => ({ type: 'agreedisagree', en: s.en, ar: s.ar }));
    const closing = pool.closing.map<Question>(q => ({ type: q.type, en: q.en, ar: q.ar }));
    const all = [...picked, ...closing];
    if (all.length === 0) { setFormError('There are no questions available right now.'); return; }

    setForm({ name, phone, major, id });
    setQuestions(all);
    setAnswers([]);
    setIndex(0);
    setSelectedIdx(null);
    setScreen('question');
  };

  const current  = questions[index];
  const isLast   = index === questions.length - 1;
  const choices  = current ? choicesFor(current.type) : [];
  const selected = selectedIdx === null ? null : choices[selectedIdx].value;

  const handleNext = () => {
    if (!selected || !current) return;
    const all = [...answers, { ...current, answer: selected }];
    setAnswers(all);
    if (isLast) {
      submit.mutate({ name: form.name, phone: form.phone, major: form.major, studentId: form.id, answers: all });
      return;
    }
    setIndex(index + 1);
    setSelectedIdx(null);
  };

  return (
    <div className="mx-auto w-full max-w-xl px-4 py-8 md:py-12">

      {screen === 'front' && (
        <div className="flex flex-col items-center">
          <svg className="mb-2 h-16 w-16" viewBox="0 0 24 24" fill="none" stroke="#16a34a"
               strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10Z" />
            <path d="M2 21c0-3 1.85-5.36 5.08-6C9.5 14.52 12 13 13 12" />
          </svg>
          <h2 className="mb-6 text-center text-2xl font-extrabold uppercase tracking-wide text-green-700 dark:text-green-400">
            Environmental Club
          </h2>

          <form onSubmit={handleStart} className={`${ui.card} flex w-full max-w-xs flex-col gap-4 p-6`}>
            {([
              ['name',  'Name',       'text'],
              ['phone', 'Number',     'tel'],
              ['major', 'Major',      'text'],
              ['id',    'Student ID', 'text'],
            ] as const).map(([key, label, type]) => (
              <div key={key}>
                <label className={ui.label} htmlFor={`q-${key}`}>{label}</label>
                <input id={`q-${key}`} className={ui.input} autoComplete="off" type={type}
                       {...(key === 'phone' ? { inputMode: 'numeric' as const } : {})}
                       value={form[key]}
                       onChange={e => { setFormError(''); setForm(f => ({ ...f, [key]: e.target.value })); }} />
              </div>
            ))}
            {formError && <p className="text-sm text-red-600 dark:text-red-400">{formError}</p>}
            <button type="submit" className={`${ui.btn} w-full py-3`}>Start</button>
          </form>
        </div>
      )}

      {screen === 'question' && current && (
        <div className="flex flex-col items-center">
          <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">
            Question {index + 1} of {questions.length}
          </p>
          <div className={`${ui.card} mb-6 flex min-h-[190px] w-full flex-col items-center justify-center gap-3 p-7`}>
            <p className="text-center text-lg font-semibold leading-relaxed text-slate-900 dark:text-slate-100">{current.en}</p>
            <p dir="rtl" className="text-center text-lg font-semibold leading-relaxed text-sky-700 dark:text-sky-400">{current.ar}</p>
          </div>

          <div className="mb-4 flex w-full gap-4">
            {choices.map((c, i) => (
              <ChoiceButton key={i} tone={c.tone} active={selectedIdx === i}
                            onClick={() => setSelectedIdx(i as 0 | 1)}>
                {c.label}
              </ChoiceButton>
            ))}
          </div>

          {submit.error && (
            <p className="mb-3 text-center text-sm text-red-600 dark:text-red-400">
              Couldn't save your answers: {errText(submit.error)}. Press the button to try again.
            </p>
          )}
          <button onClick={handleNext} disabled={!selected || submit.isPending} className={`${ui.btn} w-full py-3`}>
            {submit.isPending ? 'Saving…' : isLast ? 'Finish' : 'Next'}
          </button>
        </div>
      )}

      {screen === 'end' && (
        <div className="flex flex-col items-center">
          <div className={`${ui.card} mb-6 w-full p-8`}>
            <p className="text-center text-lg font-semibold text-slate-900 dark:text-slate-100">
              Thank you for participating! Your responses have been submitted.
            </p>
          </div>
          <button onClick={reset} className={`${ui.btn} px-10 py-3`}>Done</button>
        </div>
      )}
    </div>
  );
}

function ChoiceButton({ tone, active, onClick, children }: {
  tone: 'agree' | 'disagree'; active: boolean; onClick: () => void; children: React.ReactNode;
}) {
  const base = tone === 'agree' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700';
  const ring = active
    ? (tone === 'agree' ? 'ring-4 ring-green-300' : 'ring-4 ring-red-300')
    : '';
  return (
    <button type="button" onClick={onClick}
            className={`flex-1 rounded-xl py-3 font-bold text-white transition-colors ${base} ${ring}`}>
      {children}
    </button>
  );
}
